import {
  AppShell as MantineAppShell,
  Avatar,
  Burger,
  Group,
  Menu,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  BookOpen,
  ChartColumn,
  ClipboardList,
  Cloud,
  Database,
  FlaskConical,
  House,
  ListChecks,
  LogOut,
  Notebook,
  Scale,
  Settings,
  Stethoscope,
  CircleUser,
  UserPlus,
  Users,
  UsersRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, NavLink as RouterNavLink, Outlet, useNavigate } from "react-router-dom";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { NotificationsMenu } from "@/components/NotificationsMenu";
import { useLogout } from "@/lib/hooks/useAuth";
import { useAuthStore } from "@/stores/auth";

interface NavEntry {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  permission?: string;
}

interface NavSection {
  titleKey: string;
  entries: NavEntry[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    titleKey: "nav.section.main",
    entries: [
      { to: "/", labelKey: "nav.dashboard", icon: House },
      {
        to: "/reports",
        labelKey: "nav.reports",
        icon: ChartColumn,
        permission: "report.read.daily",
      },
      {
        to: "/settings/profile",
        labelKey: "nav.profile",
        icon: CircleUser,
      },
    ],
  },
  {
    titleKey: "nav.section.patients",
    entries: [
      { to: "/patients", labelKey: "nav.patients", icon: Users, permission: "patient.search" },
      {
        to: "/patients/new",
        labelKey: "nav.patients_new",
        icon: UserPlus,
        permission: "patient.create",
      },
    ],
  },
  {
    titleKey: "nav.section.exams",
    entries: [
      {
        to: "/worklist",
        labelKey: "nav.worklist",
        icon: ListChecks,
        permission: "section.complete.own_specialty",
      },
      {
        to: "/examinations",
        labelKey: "nav.examinations",
        icon: ClipboardList,
        permission: "examination.read",
      },
      {
        to: "/laboratory",
        labelKey: "nav.laboratory",
        icon: FlaskConical,
        permission: "lab_test.complete",
      },
    ],
  },
  {
    titleKey: "nav.section.admin",
    entries: [
      { to: "/users", labelKey: "nav.users", icon: UsersRound, permission: "user.read" },
      {
        to: "/settings/deputies",
        labelKey: "nav.deputies",
        icon: UsersRound,
        permission: "user.update",
      },
      {
        to: "/references",
        labelKey: "nav.references",
        icon: BookOpen,
        permission: "reference.read",
      },
    ],
  },
  {
    titleKey: "nav.section.system",
    entries: [
      {
        to: "/settings",
        labelKey: "nav.settings",
        icon: Settings,
        permission: "settings.read",
      },
      {
        to: "/settings/audit",
        labelKey: "nav.audit",
        icon: Scale,
        permission: "audit.read",
      },
      {
        to: "/settings/backups",
        labelKey: "nav.backups",
        icon: Database,
        permission: "backup.create",
      },
      {
        to: "/settings/sync",
        labelKey: "nav.sync",
        icon: Cloud,
        permission: "sync.status.read",
      },
    ],
  },
];

// Super admin gets a completely different sidebar — clinic-scoped nav
// items don't apply at the platform level.
const SUPER_ADMIN_NAV: NavSection[] = [
  {
    titleKey: "nav.section.platform",
    entries: [
      {
        to: "/admin",
        labelKey: "nav.admin_overview",
        icon: House,
        permission: "admin.clinic.read",
      },
      {
        to: "/admin/clinics",
        labelKey: "nav.admin_clinics",
        icon: BookOpen,
        permission: "admin.clinic.read",
      },
    ],
  },
  {
    titleKey: "nav.section.platform_data",
    entries: [
      {
        to: "/admin/users",
        labelKey: "nav.admin_users",
        icon: UsersRound,
        permission: "admin.clinic.read",
      },
      {
        to: "/admin/patients",
        labelKey: "nav.admin_patients",
        icon: Users,
        permission: "admin.clinic.read",
      },
      {
        to: "/admin/examinations",
        labelKey: "nav.admin_exams",
        icon: ClipboardList,
        permission: "admin.clinic.read",
      },
      {
        to: "/admin/forms",
        labelKey: "nav.admin_forms",
        icon: Notebook,
        permission: "admin.forms.read.any",
      },
    ],
  },
  {
    titleKey: "nav.section.platform_admin",
    entries: [
      {
        to: "/admin/audit",
        labelKey: "nav.admin_audit",
        icon: Scale,
        permission: "admin.audit.read.any",
      },
      {
        to: "/admin/billing",
        labelKey: "nav.admin_billing",
        icon: Stethoscope,
        permission: "admin.billing.read",
      },
      {
        to: "/settings/profile",
        labelKey: "nav.profile",
        icon: CircleUser,
      },
    ],
  },
];

export function AppShell() {
  const [opened, { toggle }] = useDisclosure();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const logout = useLogout();

  const onLogout = () => {
    logout.mutate(undefined, {
      onSettled: () => navigate("/login", { replace: true }),
    });
  };

  const initials = (user?.full_name ?? "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <MantineAppShell
      header={{ height: 68 }}
      navbar={{ width: 268, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="lg"
      styles={{
        main: { background: "var(--mantine-color-gray-0)" },
        header: {
          background: "white",
          borderBottom: "1px solid var(--mantine-color-gray-2)",
        },
        navbar: {
          background: "white",
          borderRight: "1px solid var(--mantine-color-gray-2)",
        },
      }}
    >
      <MantineAppShell.Header>
        <Group h="100%" px="lg" justify="space-between">
          <Group gap="md" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon
                size={42}
                radius="xl"
                variant="gradient"
                gradient={{ from: "brand.6", to: "brand.8", deg: 135 }}
              >
                <Stethoscope size={22} strokeWidth={2} color="white" />
              </ThemeIcon>
              <Stack gap={0}>
                <Text fw={800} size="md" lh={1} c="brand.9">
                  Tibbiy Ko'rik
                </Text>
                <Text c="dimmed" size="xs" lh={1.4} tt="uppercase" fw={700} mt={3}>
                  Stratix Med Pro
                </Text>
              </Stack>
            </Group>
          </Group>
          <Group gap="sm">
            <LanguageSwitcher />
            <NotificationsMenu />
            <Menu position="bottom-end" withArrow shadow="md" width={220}>
              <Menu.Target>
                <UnstyledButton
                  px={4}
                  py={4}
                  style={{
                    borderRadius: 999,
                    transition: "background 120ms ease",
                  }}
                >
                  <Group gap="xs" wrap="nowrap">
                    <Avatar
                      size={36}
                      radius="xl"
                      variant="gradient"
                      gradient={{ from: "brand.6", to: "brand.8", deg: 135 }}
                    >
                      <Text size="sm" fw={700} c="white">
                        {initials || "U"}
                      </Text>
                    </Avatar>
                    <Stack gap={0} visibleFrom="md" style={{ minWidth: 0 }}>
                      <Text size="sm" fw={700} lh={1.1} truncate>
                        {user?.full_name ?? "—"}
                      </Text>
                      <Text size="xs" c="dimmed" lh={1.2} truncate>
                        {user?.role ? t(`users.roles.${user.role}`) : ""}
                      </Text>
                    </Stack>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="xs" c="dimmed" truncate>
                    {user?.email}
                  </Text>
                </Menu.Label>
                <Menu.Item
                  leftSection={<CircleUser size={14} strokeWidth={2.2} />}
                  component={Link}
                  to="/settings/profile"
                >
                  {t("nav.profile")}
                </Menu.Item>
                <Menu.Item
                  leftSection={<Settings size={14} strokeWidth={2.2} />}
                  component={Link}
                  to="/settings"
                >
                  {t("nav.settings")}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<LogOut size={14} strokeWidth={2.2} />}
                  onClick={onLogout}
                  color="red"
                >
                  {t("nav.logout")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p={0}>
        <Stack h="100%" gap={0}>
          <ScrollArea style={{ flex: 1 }} type="auto">
            <Stack gap="lg" px="md" py="lg">
              {(user?.role === "super_admin" ? SUPER_ADMIN_NAV : NAV_SECTIONS).map((section) => {
                const visible = section.entries.filter(
                  (e) => !e.permission || permissions.includes(e.permission),
                );
                if (visible.length === 0) return null;
                return (
                  <Stack key={section.titleKey} gap={2}>
                    <Text
                      tt="uppercase"
                      c="dimmed"
                      size="xs"
                      fw={800}
                      px="sm"
                      mb={8}
                      style={{ letterSpacing: "0.08em" }}
                    >
                      {t(section.titleKey)}
                    </Text>
                    {visible.map((entry) => {
                      const Icon = entry.icon;
                      return (
                        <RouterNavLink
                          key={entry.to}
                          to={entry.to}
                          end
                          style={{ textDecoration: "none" }}
                        >
                          {({ isActive }) => (
                            <UnstyledButton
                              w="100%"
                              px="sm"
                              py={10}
                              style={{
                                borderRadius: "12px",
                                background: isActive
                                  ? "var(--mantine-color-brand-0)"
                                  : "transparent",
                                color: isActive
                                  ? "var(--mantine-color-brand-7)"
                                  : "var(--mantine-color-gray-7)",
                                fontWeight: isActive ? 700 : 500,
                                transition: "background 120ms ease",
                              }}
                            >
                              <Group gap={12} wrap="nowrap">
                                <Icon
                                  size={20}
                                  strokeWidth={isActive ? 2.4 : 2}
                                  color={
                                    isActive
                                      ? "var(--mantine-color-brand-7)"
                                      : "var(--mantine-color-gray-6)"
                                  }
                                />
                                <Text size="sm" inherit>
                                  {t(entry.labelKey)}
                                </Text>
                              </Group>
                            </UnstyledButton>
                          )}
                        </RouterNavLink>
                      );
                    })}
                  </Stack>
                );
              })}
            </Stack>
          </ScrollArea>

        </Stack>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>
        <Outlet />
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
